/**
 * iOS serve-toss trajectory heuristics for the web chart view.
 *
 * Port of ServeTossTrajectoryDetector + ServeAIDetectionConfig (generalized
 * club-wide vy template). Production iOS path is wrist upward `vy` sliding
 * windows — not ball bounce / service-box landing.
 *
 * Keep in sync with Backend/src/utils/serveHeuristics.js
 */

import { normalizeTableCorners } from "./tableCorners";
import { attachServePositionMetrics } from "./servePositionMetrics";

const L_WRIST = 9;
const R_WRIST = 10;
const MIN_JOINT_SCORE = 0.2;

/** Same analysis space as iOS TableBallActivitySampler (320×180). */
const ANALYSIS_WIDTH = 320;
const ANALYSIS_HEIGHT = 180;
const ANALYSIS_DIAGONAL = Math.hypot(ANALYSIS_WIDTH, ANALYSIS_HEIGHT);

const WINDOW_DURATION_S = 0.5;
const WINDOW_STEP_S = 0.25;
const SAMPLE_INTERVAL_S = 0.25;
const TOSS_CLUSTER_WINDOW_S = 0.85;
const MIN_SERVE_SPACING_S = 2.2;

/** Typical upward wrist vy for club-wide serve toss (normalized analysis space). */
const GENERALIZED_REF_PEAK_VY = -0.12;
/** Minimum upward vy for candidate windows without per-player reference. */
const GENERALIZED_MIN_PEAK_VY = -0.08;
const MIN_VY_WINDOW_SCORE = 0.48 * 0.85;
const MIN_TRAJECTORY_SCORE = 0.48;
const OTHER_SIDE_DOMINANCE_RATIO = 1.65;

const PRE_RISE_STILL_SPEED = 0.045;
const PRE_RISE_LOOKBACK_S = 0.55;
const RISE_VY_THRESHOLD = -0.012;
/** Toss must start within this distance of the player's table end. */
const MAX_SERVE_EDGE_DIST = 0.1;
/** If the wrist is over the table, it must sit on the end line (not mid-rally). */
const ON_BORDER_DIST = 0.045;
const SERVE_EDGE_LATERAL_MARGIN = 0.08;
/** ITTF-style toss: wrist must rise enough (image y decreases). */
const MIN_TOSS_Y_DELTA = 0.035;
/** Peak toss is mostly vertical (rally swings have large vx). */
const VERTICAL_TOSS_RATIO = 0.9;

function emptyDetection() {
  return {
    id: "serve_toss",
    label: "Serve toss",
    serve_count: 0,
    serve_count_a: 0,
    serve_count_b: 0,
    events: [],
  };
}

/** Court-left = Player A, court-right = Player B. */
export function servePlayerOf(event) {
  if (event?.player === "B" || event?.server_side === "right") return "B";
  return "A";
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function jointOk(j) {
  return (
    j &&
    Number.isFinite(Number(j.x)) &&
    Number.isFinite(Number(j.y)) &&
    (j.score == null || Number(j.score) >= MIN_JOINT_SCORE)
  );
}

function orientationSteps(viewSetup) {
  if (!viewSetup || typeof viewSetup !== "object") return [];
  if (viewSetup.metadata || viewSetup.user) {
    return [viewSetup.metadata, viewSetup.user].filter(
      (s) =>
        s &&
        (Number(s.rotation_deg || s.rotationDeg) ||
          s.flip_h ||
          s.flipH ||
          s.flip_v ||
          s.flipV)
    );
  }
  if (
    Number(viewSetup.rotation_deg || viewSetup.rotationDeg) ||
    viewSetup.flip_h ||
    viewSetup.flipH ||
    viewSetup.flip_v ||
    viewSetup.flipV
  ) {
    return [viewSetup];
  }
  return [];
}

function viewSetupMirrorsX(viewSetup) {
  let mirrors = 0;
  for (const step of orientationSteps(viewSetup)) {
    if (step.flip_h || step.flipH) mirrors += 1;
    const rot = Number(step.rotation_deg ?? step.rotationDeg ?? 0) % 360;
    if (rot === 180) mirrors += 1;
  }
  return mirrors % 2 === 1;
}

/**
 * Toss wrist = opposite the racket arm (court-left → right racket → left toss).
 */
function tossWristIndex(courtSide, viewSetup) {
  let racketRight = courtSide === "left";
  if (viewSetupMirrorsX(viewSetup)) racketRight = !racketRight;
  return racketRight ? L_WRIST : R_WRIST;
}

function tossHandName(courtSide, viewSetup) {
  return tossWristIndex(courtSide, viewSetup) === R_WRIST ? "right" : "left";
}

function parseTableCorners(raw) {
  const pts = normalizeTableCorners(raw);
  if (!pts) return null;
  const sorted = [...pts].sort((a, b) => a.y - b.y || a.x - b.x);
  const top = sorted.slice(0, 2).sort((a, b) => a.x - b.x);
  const bottom = sorted.slice(2).sort((a, b) => a.x - b.x);
  const tl = top[0];
  const tr = top[1];
  const bl = bottom[0];
  const br = bottom[1];
  return {
    leftEnd: { ax: tl.x, ay: tl.y, bx: bl.x, by: bl.y },
    rightEnd: { ax: tr.x, ay: tr.y, bx: br.x, by: br.y },
    polygon: [tl, tr, br, bl],
    centerX: (tl.x + bl.x + tr.x + br.x) / 4,
    centerY: (tl.y + bl.y + tr.y + br.y) / 4,
  };
}

function pointInQuad(x, y, polygon) {
  if (!Array.isArray(polygon) || polygon.length < 3) return false;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;
    const intersects =
      yi > y !== yj > y &&
      x < ((xj - xi) * (y - yi)) / Math.max(yj - yi, 1e-9) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

function distanceToSegmentNorm(xNorm, yNorm, seg) {
  const ax = seg.ax * ANALYSIS_WIDTH;
  const ay = seg.ay * ANALYSIS_HEIGHT;
  const bx = seg.bx * ANALYSIS_WIDTH;
  const by = seg.by * ANALYSIS_HEIGHT;
  const px = xNorm * ANALYSIS_WIDTH;
  const py = yNorm * ANALYSIS_HEIGHT;
  const abx = bx - ax;
  const aby = by - ay;
  const apx = px - ax;
  const apy = py - ay;
  const ab2 = abx * abx + aby * aby;
  const t = ab2 > 1e-9 ? Math.max(0, Math.min(1, (apx * abx + apy * aby) / ab2)) : 0;
  const dx = px - (ax + abx * t);
  const dy = py - (ay + aby * t);
  return Math.hypot(dx, dy) / ANALYSIS_WIDTH;
}

/**
 * Toss must start still, at that player's table end — on/behind the border,
 * not over the table (rally).
 */
function passesPlayerTableBorderGate(anchor, courtSide, table) {
  if (!table || !anchor) return false;
  const x = anchor.wristXNorm;
  const y = anchor.wristYNorm;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
  if (courtSide === "right" ? x < table.centerX : x > table.centerX) return false;
  const edge = courtSide === "right" ? table.rightEnd : table.leftEnd;
  const dist = distanceToSegmentNorm(x, y, edge);
  if (dist > MAX_SERVE_EDGE_DIST) return false;
  if (pointInQuad(x, y, table.polygon) && dist > ON_BORDER_DIST) return false;
  const dx = Math.abs(edge.ax - edge.bx);
  const dy = Math.abs(edge.ay - edge.by);
  if (dy >= dx) {
    const lo = Math.min(edge.ay, edge.by) - SERVE_EDGE_LATERAL_MARGIN;
    const hi = Math.max(edge.ay, edge.by) + SERVE_EDGE_LATERAL_MARGIN;
    if (y < lo || y > hi) return false;
  } else {
    const lo = Math.min(edge.ax, edge.bx) - SERVE_EDGE_LATERAL_MARGIN;
    const hi = Math.max(edge.ax, edge.bx) + SERVE_EDGE_LATERAL_MARGIN;
    if (x < lo || x > hi) return false;
  }
  return true;
}

function magnitude(sample) {
  if (!Number.isFinite(sample?.vx) || !Number.isFinite(sample?.vy)) return 0;
  return Math.hypot(sample.vx, sample.vy);
}

function playerWristTrack(samples, courtSide, wristIndex) {
  const key = courtSide === "right" ? "right" : "left";
  const out = [];
  for (const sample of samples) {
    const tMs = num(sample?.t_ms);
    if (tMs == null) continue;
    const joints = sample?.[key]?.joints;
    if (!Array.isArray(joints) || joints.length <= wristIndex) continue;
    const wrist = joints[wristIndex];
    if (!jointOk(wrist)) continue;
    out.push({
      timeSeconds: tMs / 1000,
      wristXNorm: Number(wrist.x),
      wristYNorm: Number(wrist.y),
      vx: null,
      vy: null,
      ay: null,
      serverSide: courtSide,
    });
  }
  return out;
}

function subsampleTrack(track, interval) {
  if (!track.length) return [];
  const lastT = track[track.length - 1].timeSeconds;
  const out = [];
  for (let t = 0; t <= lastT + interval * 0.01; t += interval) {
    let best = track[0];
    let bestDist = Math.abs(best.timeSeconds - t);
    for (let i = 1; i < track.length; i += 1) {
      const d = Math.abs(track[i].timeSeconds - t);
      if (d < bestDist) {
        best = track[i];
        bestDist = d;
      }
    }
    out.push({ ...best });
  }
  return out;
}

function withKinematics(grid) {
  if (grid.length < 2 || !(ANALYSIS_DIAGONAL > 1)) return [];
  const aligned = [];
  let previous = null;
  let previousVy = null;
  for (const sample of grid) {
    if (!previous) {
      aligned.push({ ...sample, vx: null, vy: null, ay: null });
      previous = sample;
      continue;
    }
    const dt = sample.timeSeconds - previous.timeSeconds;
    if (!(dt > 1e-6)) continue;
    const dx = (sample.wristXNorm - previous.wristXNorm) * ANALYSIS_WIDTH;
    const dy = (sample.wristYNorm - previous.wristYNorm) * ANALYSIS_HEIGHT;
    const vx = dx / (ANALYSIS_DIAGONAL * dt);
    const vy = dy / (ANALYSIS_DIAGONAL * dt);
    const ay = previousVy != null ? (vy - previousVy) / dt : null;
    aligned.push({ ...sample, vx, vy, ay });
    previous = sample;
    previousVy = vy;
  }
  return aligned;
}

function buildTossTrack(samples, courtSide, viewSetup) {
  const raw = playerWristTrack(
    samples,
    courtSide,
    tossWristIndex(courtSide, viewSetup)
  );
  return withKinematics(subsampleTrack(raw, SAMPLE_INTERVAL_S));
}

function riseStartIndex(track, peakIndex) {
  let j = peakIndex;
  while (j > 0) {
    if (!Number.isFinite(track[j].vy)) return Math.min(j + 1, peakIndex);
    const vy = track[j].vy ?? 0;
    if (vy >= RISE_VY_THRESHOLD) return Math.min(j + 1, peakIndex);
    j -= 1;
  }
  return 0;
}

/** Lowest still wrist sample before the upward toss (ball on palm). Rally has no still. */
function preRiseStillSample(track, peakTime) {
  if (!track.length) return null;
  let peakIndex = 0;
  let peakDist = Math.abs(track[0].timeSeconds - peakTime);
  for (let i = 1; i < track.length; i += 1) {
    const d = Math.abs(track[i].timeSeconds - peakTime);
    if (d < peakDist) {
      peakIndex = i;
      peakDist = d;
    }
  }
  const riseStartIdx = riseStartIndex(track, peakIndex);
  const riseStart = track[riseStartIdx];
  const windowStartTime = riseStart.timeSeconds - PRE_RISE_LOOKBACK_S;

  let bestStill = null;
  let bestStillY = -Infinity;

  for (let index = riseStartIdx; index >= 0; index -= 1) {
    const sample = track[index];
    if (sample.timeSeconds < windowStartTime) break;
    if (
      Number.isFinite(sample.vx) &&
      Number.isFinite(sample.vy) &&
      magnitude(sample) <= PRE_RISE_STILL_SPEED &&
      sample.wristYNorm > bestStillY
    ) {
      bestStillY = sample.wristYNorm;
      bestStill = sample;
    }
  }
  return bestStill;
}

function evaluateVyWindow(
  windowSamples,
  serverTrack,
  refVy,
  minVy,
  table,
  courtSide
) {
  const effectiveMinVy = minVy;
  const qualifying = windowSamples.filter((sample) => {
    if (!Number.isFinite(sample.vx) || !Number.isFinite(sample.vy)) return false;
    const vy = sample.vy;
    return vy < 0 && vy <= effectiveMinVy;
  });
  if (!qualifying.length) return null;
  let peak = qualifying[0];
  for (let i = 1; i < qualifying.length; i += 1) {
    if ((qualifying[i].vy ?? 0) < (peak.vy ?? 0)) peak = qualifying[i];
  }
  const peakVy = peak.vy ?? 0;
  const peakVx = peak.vx ?? 0;
  if (!(Math.abs(peakVy) >= Math.abs(peakVx) * VERTICAL_TOSS_RATIO)) return null;
  const vyScore = Math.min(1, Math.abs(peakVy) / Math.max(Math.abs(refVy), 0.05));
  if (vyScore < MIN_VY_WINDOW_SCORE) return null;
  const anchor = preRiseStillSample(serverTrack, peak.timeSeconds);
  if (!anchor) return null;
  const yDelta = Number(anchor.wristYNorm) - Number(peak.wristYNorm);
  if (!(yDelta >= MIN_TOSS_Y_DELTA)) return null;
  if (!passesPlayerTableBorderGate(anchor, courtSide, table)) return null;
  return { peak, anchor, vyScore };
}

function otherVyWindowScore(time, otherTrack, refVy, minVy, table, otherSide) {
  const windowStart = time - WINDOW_DURATION_S * 0.5;
  const windowEnd = time + WINDOW_DURATION_S * 0.5;
  const windowSamples = otherTrack.filter(
    (s) => s.timeSeconds >= windowStart && s.timeSeconds <= windowEnd
  );
  return (
    evaluateVyWindow(
      windowSamples,
      otherTrack,
      refVy,
      minVy,
      table,
      otherSide
    )?.vyScore ?? null
  );
}

function clusterBestCandidates(ranked, windowSeconds) {
  const sorted = [...ranked].sort((a, b) => a.timeSeconds - b.timeSeconds);
  const clusters = [];
  for (const candidate of sorted) {
    const last = clusters[clusters.length - 1];
    if (
      last &&
      candidate.timeSeconds - last[last.length - 1].timeSeconds < windowSeconds
    ) {
      last.push(candidate);
    } else {
      clusters.push([candidate]);
    }
  }
  return clusters
    .map((cluster) =>
      cluster.reduce((best, c) =>
        c.trajectoryScore > best.trajectoryScore ? c : best
      )
    )
    .filter(Boolean);
}

function selectSpacedPeaks(ranked, minScore, minSpacing) {
  const passing = ranked
    .filter((c) => c.trajectoryScore >= minScore)
    .sort((a, b) => a.timeSeconds - b.timeSeconds);
  const kept = [];
  for (const candidate of passing) {
    const last = kept[kept.length - 1];
    if (last && candidate.timeSeconds - last.timeSeconds < minSpacing) {
      if (candidate.trajectoryScore > last.trajectoryScore) {
        kept[kept.length - 1] = candidate;
      }
    } else {
      kept.push(candidate);
    }
  }
  return kept;
}

function detectSide(serverTrack, otherTrack, durationSeconds, table, courtSide) {
  const refVy = GENERALIZED_REF_PEAK_VY;
  if (!(refVy < -0.05)) return [];
  const minVy = GENERALIZED_MIN_PEAK_VY;
  const otherSide = courtSide === "right" ? "left" : "right";
  const ranked = [];
  let windowStart = 0;
  const lastWindowStart = Math.max(0, durationSeconds - WINDOW_DURATION_S);

  while (windowStart <= lastWindowStart + SAMPLE_INTERVAL_S * 0.5) {
    const windowEnd = windowStart + WINDOW_DURATION_S;
    const windowSamples = serverTrack.filter(
      (s) => s.timeSeconds >= windowStart && s.timeSeconds <= windowEnd
    );
    const scored = evaluateVyWindow(
      windowSamples,
      serverTrack,
      refVy,
      minVy,
      table,
      courtSide
    );
    if (scored) {
      const otherScore = otherVyWindowScore(
        scored.peak.timeSeconds,
        otherTrack,
        refVy,
        minVy,
        table,
        otherSide
      );
      if (otherScore != null) {
        if (
          otherScore > 0.15 &&
          scored.vyScore < otherScore * OTHER_SIDE_DOMINANCE_RATIO
        ) {
          windowStart += WINDOW_STEP_S;
          continue;
        }
        if (scored.vyScore < otherScore + 0.06) {
          windowStart += WINDOW_STEP_S;
          continue;
        }
      }
      ranked.push({
        timeSeconds: scored.peak.timeSeconds,
        trajectoryScore: scored.vyScore,
        peak: scored.peak,
        anchor: scored.anchor,
      });
    }
    windowStart += WINDOW_STEP_S;
  }

  ranked.sort((a, b) => b.trajectoryScore - a.trajectoryScore);
  const clustered = clusterBestCandidates(ranked, TOSS_CLUSTER_WINDOW_S);
  return selectSpacedPeaks(clustered, MIN_TRAJECTORY_SCORE, MIN_SERVE_SPACING_S);
}

/** Toss rise to peak when rise start is unknown. */
const SERVE_TOSS_RISE_MS = 450;
/** Toss peak → contact (descent + swing to service). */
const SERVE_SWING_AFTER_PEAK_MS = 550;
const SERVE_CONTACT_SPAN_MS = 150;
/** After recovery: far-side flight, 1st+2nd bounce, then out. */
export const SERVE_FLIGHT_AFTER_RECOVERY_MS = 2500;
/** Contact → end of recovery (peak + 550 + 150 + 1100 = peak + 1800). */
const SERVE_RECOVERY_MS = 1100;
const SERVE_WINDOW_MIN_MS = 80;

/**
 * Serve states: preparation (steady toss hand, opposite the racket) → toss →
 * swing → contact → recovery. Prep starts at the still toss-hand sample.
 */
export function servePhasesFromTimes(peakMs, steadyMs) {
  const T = Number(peakMs);
  if (!Number.isFinite(T) || T < 0) return null;
  const Sraw = Number(steadyMs);
  const S = Number.isFinite(Sraw)
    ? Math.max(0, Math.min(Sraw, T))
    : Math.max(0, T - SERVE_TOSS_RISE_MS - 100);
  const tossStart = Math.min(T, Math.max(S, T - SERVE_TOSS_RISE_MS));
  const swingStart = T + 150;
  const contactStart = T + SERVE_SWING_AFTER_PEAK_MS;
  const contactEnd = contactStart + SERVE_CONTACT_SPAN_MS;
  const recoveryEnd = contactEnd + SERVE_RECOVERY_MS;
  const ms = (n) => Math.round(n);
  return {
    preparation: { t_start_ms: ms(S), t_end_ms: ms(tossStart) },
    toss: { t_start_ms: ms(tossStart), t_end_ms: ms(swingStart) },
    swing: { t_start_ms: ms(swingStart), t_end_ms: ms(contactStart) },
    contact: { t_start_ms: ms(contactStart), t_end_ms: ms(contactEnd) },
    recovery: { t_start_ms: ms(contactEnd), t_end_ms: ms(recoveryEnd) },
  };
}

/** Ball-detection window: still toss-hand prep → recovery + far-side flight. */
export function serveBallWindowFromEvent(event) {
  if (!event || typeof event !== "object") return null;
  const stored = event.phases && typeof event.phases === "object" ? event.phases : null;
  const phases =
    stored?.preparation && stored?.recovery
      ? stored
      : servePhasesFromTimes(
          event.t_peak_ms ?? event.t_ms ?? event.t_contact_ms,
          event.t_steady_ms ?? event.t_pre_rise_ms
        );
  if (!phases) return null;
  const t0 = Number(phases.preparation?.t_start_ms);
  const recEnd = Number(phases.recovery?.t_end_ms ?? phases.recovery?.t_start_ms);
  if (!Number.isFinite(t0) || !Number.isFinite(recEnd)) return null;
  const t1 = recEnd + SERVE_FLIGHT_AFTER_RECOVERY_MS;
  if (t1 - t0 < SERVE_WINDOW_MIN_MS) return null;
  return { t0: Math.round(t0), t1: Math.round(t1) };
}

/**
 * Serve windows clipped so tracking stops when the next serve prep starts.
 */
export function serveBallWindowsFromEvents(events) {
  const wins = (Array.isArray(events) ? events : [])
    .map(serveBallWindowFromEvent)
    .filter(Boolean)
    .sort((a, b) => a.t0 - b.t0);
  for (let i = 0; i < wins.length - 1; i += 1) {
    wins[i] = {
      t0: wins[i].t0,
      t1: Math.min(wins[i].t1, wins[i + 1].t0),
    };
  }
  return wins.filter((w) => w.t1 - w.t0 >= SERVE_WINDOW_MIN_MS);
}

function eventFromCandidate(candidate, serverSide, tossHand) {
  const sample = candidate.peak;
  const anchor = candidate.anchor;
  const tSec = candidate.timeSeconds;
  const peakMs = Math.round(tSec * 1000);
  const steadyMs = Number.isFinite(anchor?.timeSeconds)
    ? Math.round(anchor.timeSeconds * 1000)
    : null;
  const phases = servePhasesFromTimes(peakMs, steadyMs);
  return {
    t_ms: peakMs,
    t_peak_ms: peakMs,
    t_steady_ms: steadyMs,
    t_prep_start_ms: phases?.preparation?.t_start_ms ?? null,
    t_recovery_end_ms: phases?.recovery?.t_end_ms ?? null,
    confidence: candidate.trajectoryScore,
    server_side: serverSide,
    player: serverSide === "right" ? "B" : "A",
    toss_hand: tossHand,
    source: "trajectory",
    wrist_x_norm: sample?.wristXNorm ?? null,
    wrist_y_norm: sample?.wristYNorm ?? null,
    velocity_x: sample?.vx ?? null,
    velocity_y: sample?.vy ?? null,
    pre_rise_x_norm: anchor?.wristXNorm ?? null,
    pre_rise_y_norm: anchor?.wristYNorm ?? null,
    phases,
  };
}

function dedupeServes(events, spacingS) {
  if (!(spacingS > 0)) return events;
  const sorted = [...events].sort((a, b) => a.t_ms - b.t_ms);
  const kept = [];
  for (const event of sorted) {
    const last = kept[kept.length - 1];
    if (last && event.t_ms / 1000 - last.t_ms / 1000 < spacingS) {
      if (event.confidence > last.confidence) kept[kept.length - 1] = event;
    } else {
      kept.push(event);
    }
  }
  return kept;
}

/**
 * Detect serve tosses from pose samples (Player A = court-left, Player B = court-right).
 * Both screen sides are scanned with the same toss-wrist + vy-window rules as iOS,
 * then merged with 2.2s NMS.
 */
export function detectServes(samples, viewSetup = null, options = null) {
  const ordered = Array.isArray(samples)
    ? [...samples].sort((a, b) => (Number(a.t_ms) || 0) - (Number(b.t_ms) || 0))
    : [];
  if (ordered.length < 3) return emptyDetection();

  const table = parseTableCorners(
    options?.tableCorners || options?.table_corners || options?.corners_norm
  );
  const durationSeconds =
    (Number(ordered[ordered.length - 1].t_ms) || 0) / 1000;
  const leftTrack = buildTossTrack(ordered, "left", viewSetup);
  const rightTrack = buildTossTrack(ordered, "right", viewSetup);
  const leftHand = tossHandName("left", viewSetup);
  const rightHand = tossHandName("right", viewSetup);

  const leftEvents = detectSide(
    leftTrack,
    rightTrack,
    durationSeconds,
    table,
    "left"
  ).map((c) => eventFromCandidate(c, "left", leftHand));
  const rightEvents = detectSide(
    rightTrack,
    leftTrack,
    durationSeconds,
    table,
    "right"
  ).map((c) => eventFromCandidate(c, "right", rightHand));

  let playerIndexA = 0;
  let playerIndexB = 0;
  const events = dedupeServes(
    [...leftEvents, ...rightEvents],
    MIN_SERVE_SPACING_S
  ).map((event, i) => {
    const player = servePlayerOf(event);
    if (player === "B") playerIndexB += 1;
    else playerIndexA += 1;
    return {
      ...event,
      index: i + 1,
      player,
      player_index: player === "B" ? playerIndexB : playerIndexA,
      server_side: player === "B" ? "right" : "left",
    };
  });

  return attachServePositionMetrics(
    {
      id: "serve_toss",
      label: "Serve toss",
      serve_count: events.length,
      serve_count_a: playerIndexA,
      serve_count_b: playerIndexB,
      events,
    },
    ordered,
    options?.tableCorners || options?.table_corners || options?.corners_norm
  );
}

export const SERVE_HEURISTIC_ID = "serve_toss";
export const SERVE_DETECTION_CONSTANTS = {
  SAMPLE_INTERVAL_S,
  MIN_SERVE_SPACING_S,
  GENERALIZED_REF_PEAK_VY,
  GENERALIZED_MIN_PEAK_VY,
  MAX_SERVE_EDGE_DIST,
};
